import { Apps } from 'src/app/shared/enums/enums';
import { PermissionTemplate } from '../../setting/models/permission-template.model';

/**
 * API Response model for GET /Apps endpoint
 * Represents the raw data from the server
 */
export interface AppResponse {
  id: number;
  name: string;
}

/**
 * Lightweight state model for displaying apps list in v2 components
 * Contains essential fields for UI display including client-side added appIcon
 */
export interface AppLiteState {
  id: number;
  name: string;
  appIcon: string;
  isAppSelect?: boolean; // UI state for selection in app picker
  isDisabled?: boolean;
}

/**
 * Map of app IDs to their icon paths
 */
const APP_ICON_MAP: { [key: number]: string } = {
  1: 'assets/images/subscription/TPMLogo.png',
  2: 'assets/images/subscription/POSLogo.png',
  3: 'assets/images/users-management/donationlogo.svg',
  4: 'assets/images/subscription/ListerLogo.png',
};

/**
 * Transform AppResponse to AppLiteState with icon
 * @param app API response
 * @returns State model with icon
 */
export function toAppLiteState(app: AppResponse): AppLiteState {
  return {
    id: app.id,
    name: app.name,
    appIcon: APP_ICON_MAP[app.id] || '',
  };
}

/**
 * Transform array of AppResponse to AppLiteState array
 * Filters to only include apps with IDs < 5
 * @param apps Array of API responses
 * @returns Array of state models
 */
export function toAppLiteStateArray(apps: AppResponse[]): AppLiteState[] {
  return apps.filter((app) => app.id < Apps.AdminPortal).map((app) => toAppLiteState(app));
}

export class AppsModel {
  id: number = 0;
  name: string = '';
  description: string = '';
  canShowOnWebsite: boolean = false;
  type: number = 1;
  companyId: number = 0;
  appIcon: string = '';
  licenses: Array<LicensesModel> = [];
  items: Array<LicensesModel> = [];
  plansList: Array<PlansOverview> = [];
  isAppSelect: boolean = false;
  status: number = 0;
  appsIcons: string[] = [];
  subscriptionCount: number = 0;
}

export class LicensesModel {
  id: number = 0;
  licenseId: number = 0;
  monthlyPrice: number = 0;
  appId: number = 0;
  orderId: number = 0;
  fieldName: string = '';
  licenseType: number = 0;
  featureDescription: string = '';
  permissionTemplate: PermissionTemplate = new PermissionTemplate();
  licenseAttributes: Array<LicenseAttributesModel> = [];
}

export class LicenseAttributesModel {
  id: number = 0;
  licenseId: number = 0;
  licenseAttributeType: number = 0;
  licenseAttributeId: number = 0;
  label: string = '';
  order: number = 0;
  defaultValue: any;
  isEditPrice: boolean = false;
  dataType: string = '';
  isVisibleOnDefaultPriceSetup: boolean = false;
  isVisibleOnSubscriptionPlan: boolean = false;
  isEditableOnSubscriptionPlan: boolean = false;
  isVisibleOnSubscription: boolean = false;
  isEditableOnSubscription: boolean = false;
}

export class PlansOverview {
  monthlyCostLabel: string = '';
  monthlyCost: number = 0;
  yearlyCostLabel: string = '';
  yearlyCost: number = 0;
  isDiscount: boolean = false;
  discountVal: any;
  discountType: number = 1;
  discountCrudVal: string = 'apply';
}

